// Design tokens: defines the --tl-* custom properties + loads self-hosted fonts.
// Imported first so app styles can still override; nothing consumes the tokens yet.
import '@repo/design-tokens/tokens.css';
// Global CSS reset (vendored Tailwind v3.4 preflight, formerly `@tailwind base`); loads before
// App.css so app styles override it.
import '@repo/design-tokens/preflight.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { Auth0ProviderWithNavigate } from './Auth0ProviderWithNavigate';
import reportWebVitals from './reportWebVitals';
import {
  initUiSettingsFromEnv,
  initUserStoreFromStorage,
  initMariaConfigFromEnv,
  FilterAndModeContextsProvider,
  useTwinStore,
  useUserStore,
} from '@repo/front-end-state';
import {
  initClientPerfReporter,
  installRequestIdInterceptor,
  reportClientPerf,
} from '@repo/front-end-services';
import { installEquipmentLoadEnvelopes, onMeasure } from '@repo/front-end-utils';
import Config from './common/Config';

// Apply environment-specific viewer params to the UiSettingsStore
initUiSettingsFromEnv(process.env.REACT_APP_VIEWER_PARAMS);

// Restore user organisation data from localStorage
initUserStoreFromStorage();

/*
 * DEV/TEST ONLY — expose the user store so a Playwright walk can put the app into an auth state
 * that no available login can produce.
 *
 * WHY IT HAS TO EXIST. Every role-gated or org-gated surface in this app reads `authMetadata`,
 * which is derived from Auth0 claims and cannot be reshaped from the outside. Auth0 staging holds
 * exactly one non-anonymous test identity and it is a TL_ADMIN, so the customer-facing side of any
 * such gate — the side real customers see — has no login behind which to observe it. Without this
 * hook, first-line visual validation of a customer gate is not merely hard, it is unavailable, and
 * the alternative is handing it to a person, which this repo forbids.
 *
 * Substituting the identity at the STORE is the narrowest possible double: everything downstream —
 * MainNav, the mode dropdown, the twin config, the 3D scene — is the real thing, exercised for
 * real. Stripped from production builds by the guard, so it ships nothing.
 */
if (process.env.NODE_ENV !== 'production') {
  (window as unknown as { __twinUserStore?: unknown }).__twinUserStore = useUserStore;
}

// Initialise Maria AI endpoint from the same env var Config.middlewareApi wraps
initMariaConfigFromEnv(process.env.REACT_APP_API_SERVER_URL);

// Stamp `x-request-id` on every outbound axios call, so a slow browser request can be joined to the
// middleware and query-service log lines for the SAME request (equipment-performance R3).
installRequestIdInterceptor(Config.middlewareApi);

// Fluent Provider
import { FluentProvider, PartialTheme, teamsLightTheme } from '@fluentui/react-components';

// Theme
import { lightTheme } from '@repo/front-end-data';

import { AuthMetadataRestorer } from './components/Auth0/AuthMetadataRestorer';

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

const theme: PartialTheme = {
  ...teamsLightTheme,
  ...lightTheme,
  fontFamilyBase: '"Inter", sans-serif',
};

root.render(
  <React.StrictMode>
    <FluentProvider theme={theme}>
      <FilterAndModeContextsProvider>
        <BrowserRouter>
          <Auth0ProviderWithNavigate>
            <AuthMetadataRestorer>
              <App />
            </AuthMetadataRestorer>
          </Auth0ProviderWithNavigate>
        </BrowserRouter>
      </FilterAndModeContextsProvider>
    </FluentProvider>
  </React.StrictMode>,
);

// Core Web Vitals, finally reaching a real handler (equipment-performance R1). This call previously
// passed NO handler, so every metric web-vitals gathered was discarded — the instrumentation was
// present, wired, and dead. Now on web-vitals v4, so INP (the successor to FID, and the metric that
// actually describes "the panel feels unresponsive") is included.
//
// The reporter buffers and flushes to `POST /api/twins/:bid/client-perf`; it holds entries until a twin
// is selected, because the ingest route needs a guard-verified twin BID in its path.
initClientPerfReporter({
  baseUrl: Config.middlewareApi,
  app: 'twin-viewer',
  // Read lazily on each flush: both the token (which refreshes) and the twin (which the user
  // switches) change during a session, so capturing either by value here would go stale.
  //
  // `useTwinStore.selectedTwinBID` rather than `useTwinContext().twin` — the parsed twin lives in a
  // React CONTEXT, which cannot be read from a module-level callback. The Zustand store's BID is the
  // same selection, reachable outside the component tree, and a BID is all the ingest route needs.
  getAccessToken: () => useUserStore.getState().authMetadata?.tokens?.accessToken,
  getTwinBid: () => useTwinStore.getState().selectedTwinBID ?? undefined,
});

// Close the two ENVELOPE measures (`equipment:total`, `equipment:fetch`) — they depend on measures
// that complete in components which do not know about each other, so the coordination lives in one
// place in @repo/front-end-utils rather than being spread across three of them.
installEquipmentLoadEnvelopes();

// Forward every completed phase measure to the same ingest the vitals use. Wired here, rather than
// imported inside @repo/front-end-utils, because front-end-services already depends on
// front-end-utils — importing back would be a package cycle. `onMeasure` inverts the dependency.
onMeasure(measure => {
  reportClientPerf({
    kind: 'phase',
    name: measure.name,
    valueMs: measure.durationMs,
    mode: 'EQUIPMENT',
  });
});

reportWebVitals(metric => {
  reportClientPerf({
    kind: 'vital',
    name: metric.name,
    // CLS is a unitless score rather than a duration; the middleware routes it to its own histogram
    // on the strength of the name, so it must NOT be scaled here.
    valueMs: metric.value,
  });
});
